import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Alert,
    Grid,
    FormControlLabel,
    Switch
} from '@mui/material';
import api from '../../utils/api';

const UserBranchManagement = ({ open, onClose, user, currentBranch }) => {
    const [branches, setBranches] = useState([]);
    const [formData, setFormData] = useState({
        targetBranchId: '',
        isBranchAdmin: false
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            fetchBranches();
        }
    }, [open]);

    const fetchBranches = async () => {
        try {
            const response = await api.get('/branches');
            setBranches(response.data.branches || []);
        } catch (err) {
            setError('Failed to load branches');
        }
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'isBranchAdmin' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (currentBranch) {
                // Transfer user to new branch
                await api.post('/user-branches/transfer', {
                    userId: user.id,
                    fromBranchId: currentBranch,
                    toBranchId: formData.targetBranchId,
                    isBranchAdmin: formData.isBranchAdmin
                });
            } else {
                // Assign user to new branch
                await api.post('/user-branches/assign', {
                    userId: user.id,
                    branchId: formData.targetBranchId,
                    isBranchAdmin: formData.isBranchAdmin
                });
            }
            onClose(true); // Pass true to indicate success
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update branch assignment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {currentBranch ? 'Transfer User to Another Branch' : 'Assign User to Branch'}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                {user?.firstName} {user?.lastName} ({user?.email})
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Target Branch</InputLabel>
                                <Select
                                    name="targetBranchId"
                                    value={formData.targetBranchId}
                                    onChange={handleChange}
                                    label="Target Branch"
                                    sx={{ minHeight: 56 }}
                                >
                                    {branches
                                        .filter(branch => branch.id !== currentBranch)
                                        .map(branch => (
                                            <MenuItem key={branch.id} value={branch.id}>
                                                {branch.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isBranchAdmin}
                                        onChange={handleChange}
                                        name="isBranchAdmin"
                                    />
                                }
                                label="Assign as Branch Admin"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose(false)}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formData.targetBranchId}
                    >
                        {currentBranch ? 'Transfer' : 'Assign'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default UserBranchManagement; 